import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Send, MessagesSquare, ArrowLeft, Share2, ShieldCheck, FileCheck2, Check, X, Users, Star, ThumbsUp, ThumbsDown, Quote } from "lucide-react";

const REF_TYPES = [
  { value: "Yrkesreferens", label: "Yrkesreferenser" },
  { value: "Karaktärsreferens", label: "Karaktärsreferenser" },
];

function RefRequestCard({ req, me, onRespond, convId }) {
  const [busy, setBusy] = useState("");
  const [refs, setRefs] = useState(null);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const label = (req?.types || []).map((t) => (t === "Yrkesreferens" ? "yrkesreferenser" : "karaktärsreferenser")).join(" och ");

  const respond = async (action) => {
    setBusy(action);
    try { await onRespond(req.id, action); }
    finally { setBusy(""); }
  };

  const viewRefs = async () => {
    setLoadingRefs(true);
    try { const { data } = await api.get(`/conversations/${convId}/references`); setRefs(data.references || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingRefs(false); }
  };

  return (
    <div className="flex justify-center" data-testid={`ref-request-${req.id}`}>
      <div className="max-w-[90%] w-full rounded-2xl border border-accent/30 bg-accent/[0.06] p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-1"><FileCheck2 className="h-4 w-4 text-accent" /> Samtycke: referenser</div>
        <p className="text-sm text-muted-foreground">Företaget ber om lov att kontakta dina <strong>{label}</strong>.</p>
        {req.status === "pending" && me === "candidate" && (
          <div className="flex gap-2 mt-3">
            <Button size="sm" className="rounded-full" onClick={() => respond("approve")} disabled={!!busy} data-testid={`ref-approve-${req.id}`}>
              {busy === "approve" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />} Godkänn
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => respond("deny")} disabled={!!busy} data-testid={`ref-deny-${req.id}`}>
              <X className="h-4 w-4 mr-1.5" /> Avböj
            </Button>
          </div>
        )}
        {req.status === "pending" && me === "employer" && <p className="text-xs text-muted-foreground mt-2">Väntar på kandidatens svar…</p>}
        {req.status === "denied" && <p className="text-xs text-destructive mt-2">Kandidaten avböjde.</p>}
        {req.status === "approved" && (
          <div className="mt-3">
            <p className="text-xs text-primary font-medium mb-2 flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Godkänt av kandidaten</p>
            {me === "employer" && (
              refs === null ? (
                <Button size="sm" variant="outline" className="rounded-full" onClick={viewRefs} disabled={loadingRefs} data-testid={`ref-view-${req.id}`}>
                  {loadingRefs ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Users className="h-4 w-4 mr-1.5" />} Visa delade referenser
                </Button>
              ) : refs.length === 0 ? (
                <p className="text-xs text-muted-foreground">Kandidaten har inte lagt till några referenser av denna typ ännu.</p>
              ) : (
                <div className="grid gap-2">
                  {[...refs].sort((a, b) => (b.status === "received") - (a.status === "received")).map((r, i) => {
                    const responded = r.status === "received";
                    const invited = r.status === "sent" || r.status === "link_ready";
                    const st = responded
                      ? { label: "Har svarat", cls: "bg-primary/10 text-primary border-primary/25" }
                      : invited
                      ? { label: "Inbjuden – inget svar än", cls: "bg-amber-100 text-amber-700 border-amber-300" }
                      : { label: "Ej inbjuden", cls: "bg-muted text-muted-foreground border-border" };
                    return (
                      <div key={i} className="rounded-xl bg-white border border-border p-3 text-sm" data-testid={`shared-ref-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium">{r.name || "Referens"} <span className="text-xs text-muted-foreground">· {r.ref_type}</span></div>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium shrink-0 ${st.cls}`} data-testid={`ref-status-${i}`}>{st.label}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">{r.relation}{r.email ? ` · ${r.email}` : ""}</div>
                        {responded && r.response && (
                          <div className="mt-2.5 border-t border-border pt-2.5 space-y-2" data-testid={`ref-answer-${i}`}>
                            <div className="flex flex-wrap items-center gap-2">
                              {r.response.rating ? (
                                <span className="flex items-center gap-0.5">
                                  {[1, 2, 3, 4, 5].map((n) => (
                                    <Star key={n} className="h-3.5 w-3.5 text-amber-500" fill={n <= r.response.rating ? "currentColor" : "none"} />
                                  ))}
                                </span>
                              ) : null}
                              {r.response.would_recommend === true && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary"><ThumbsUp className="h-3 w-3" /> Rekommenderar</span>}
                              {r.response.would_recommend === false && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground"><ThumbsDown className="h-3 w-3" /> Rekommenderar ej</span>}
                            </div>
                            {r.response.message && (
                              <p className="text-sm text-foreground/90 flex gap-1.5"><Quote className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />{r.response.message}</p>
                            )}
                            {(r.response.details || []).length > 0 && (
                              <div className="space-y-1.5">
                                {r.response.details.map((d, j) => (
                                  <div key={j} className="text-xs">
                                    <span className="font-medium text-foreground">{d.amne}:</span>{" "}
                                    <span className="text-foreground/80">{d.svar}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function ConversationsPanel({ canShareContact = false, canRequestReferences = false, emptyText = "Inga konversationer ännu." }) {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null); // conversation id
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [refDialogOpen, setRefDialogOpen] = useState(false);
  const [refTypes, setRefTypes] = useState([]);
  const [requestingRef, setRequestingRef] = useState(false);
  const endRef = useRef(null);

  const loadList = () => api.get("/conversations").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { loadList(); }, []);

  const openConv = async (id) => {
    setActive(id);
    setThread(null);
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setThread(data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${active}/messages`, { text });
      setThread((t) => ({ ...t, messages: [...t.messages, data] }));
      setText("");
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
      loadList();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const shareContact = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/conversations/${active}/share-contact`);
      setThread((t) => ({ ...t, messages: [...t.messages, data] }));
      toast.success("Dina kontaktuppgifter har delats");
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSharing(false); }
  };

  const respondRefRequest = async (reqId, action) => {
    try {
      await api.put(`/reference-requests/${reqId}`, { action });
      toast.success(action === "approve" ? "Godkänt" : "Avböjt");
      await openConv(active);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const requestReferences = async () => {
    if (refTypes.length === 0) { toast.error("Välj minst en typ"); return; }
    setRequestingRef(true);
    try {
      await api.post(`/conversations/${active}/reference-request`, { types: refTypes });
      toast.success("Referensförfrågan skickad");
      setRefDialogOpen(false);
      setRefTypes([]);
      await openConv(active);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRequestingRef(false); }
  };

  if (list === null) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" /></div>;

  // Chat view
  if (active && thread) {
    return (
      <Card className="rounded-2xl border-border overflow-hidden" data-testid="conversation-thread">
        <div className="flex items-center gap-3 border-b border-border p-4">
          <button onClick={() => { setActive(null); setThread(null); }} className="text-muted-foreground hover:text-foreground" data-testid="conversation-back"><ArrowLeft className="h-5 w-5" /></button>
          <div className="min-w-0">
            <div className="font-medium text-sm truncate">{thread.conversation.counterpart}</div>
            {thread.conversation.role_title && <div className="text-xs text-muted-foreground truncate">{thread.conversation.role_title}</div>}
          </div>
        </div>
        <div className="h-[46vh] overflow-y-auto p-4 space-y-3 bg-muted/20">
          {thread.messages.map((m) => {
            const mine = m.sender_role === thread.me;
            const system = m.sender_role === "system";
            if (m.ref_request_id) {
              const req = (thread.ref_requests || []).find((r) => r.id === m.ref_request_id);
              if (req) return <RefRequestCard key={m.id} req={req} me={thread.me} onRespond={respondRefRequest} convId={active} />;
            }
            if (system) return (
              <div key={m.id} className="text-center"><span className="inline-block rounded-full bg-primary/10 text-primary text-xs px-3 py-1.5">{m.text}</span></div>
            );
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${mine ? "bg-primary text-primary-foreground" : "bg-white border border-border"} ${m.shared_contact ? "ring-1 ring-accent" : ""}`}>
                  {m.text}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="border-t border-border p-3">
          {canRequestReferences && (
            <div className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-accent/10 px-3 py-2">
              <span className="text-xs text-muted-foreground flex items-center gap-1.5"><FileCheck2 className="h-3.5 w-3.5 text-accent" /> Be om lov att kontakta referenser.</span>
              <Dialog open={refDialogOpen} onOpenChange={setRefDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="rounded-full" data-testid="request-references-btn"><FileCheck2 className="h-4 w-4 mr-1.5" /> Be om referenser</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Be om samtycke för referenser</DialogTitle></DialogHeader>
                  <DialogDescription>Kandidaten får själv godkänna eller avböja. Först vid godkännande ser du referensernas kontaktuppgifter.</DialogDescription>
                  <div className="grid gap-2 py-2">
                    {REF_TYPES.map((t) => {
                      const checked = refTypes.includes(t.value);
                      return (
                        <button key={t.value} type="button" onClick={() => setRefTypes((s) => checked ? s.filter((x) => x !== t.value) : [...s, t.value])}
                          className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm text-left transition-colors ${checked ? "border-primary bg-primary/10 text-primary font-medium" : "border-border hover:bg-muted"}`}
                          data-testid={`ref-type-${t.value}`}>
                          <span className={`flex h-4 w-4 items-center justify-center rounded border ${checked ? "bg-primary border-primary text-white" : "border-muted-foreground/40"}`}>{checked && <Check className="h-3 w-3" />}</span>
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                  <DialogFooter>
                    <Button className="rounded-full" onClick={requestReferences} disabled={requestingRef} data-testid="send-ref-request-btn">
                      {requestingRef ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Skicka förfrågan
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          {canShareContact && (
            <div className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-accent/10 px-3 py-2">
              <span className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-accent" /> Företaget ser bara ditt HAMN-id.</span>
              <Button size="sm" variant="outline" className="rounded-full" onClick={shareContact} disabled={sharing} data-testid="share-contact-btn">
                {sharing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Share2 className="h-4 w-4 mr-1.5" />} Dela mina uppgifter
              </Button>
            </div>
          )}
          <div className="flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Skriv ett meddelande…" className="h-11" data-testid="message-input" />
            <Button className="rounded-full h-11 px-4" onClick={send} disabled={sending} data-testid="message-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // List view
  if (list.length === 0) {
    return (
      <Card className="rounded-2xl border-dashed border-border p-10 text-center" data-testid="conversations-empty">
        <MessagesSquare className="h-8 w-8 text-primary mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      </Card>
    );
  }
  return (
    <div className="grid gap-3" data-testid="conversations-list">
      {list.map((c) => (
        <Card key={c.id} className="rounded-2xl border-border p-4 flex items-center justify-between gap-3 cursor-pointer hover:border-primary/40 transition-colors"
          onClick={() => openConv(c.id)} data-testid={`conversation-${c.id}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0"><MessagesSquare className="h-5 w-5" /></div>
            <div className="min-w-0">
              <div className="font-medium text-sm truncate">{c.counterpart}{c.role_title ? ` · ${c.role_title}` : ""}</div>
              <div className="text-xs text-muted-foreground truncate">{c.last_message || "Ny konversation"}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
